import type { ReactElement } from 'react';
import type { OrderLine } from '../../shared/orderStatus';
import { StepIndicator } from './StepIndicator';
import { CoreTracker } from './CoreTracker';

interface LineTrackerProps {
  line: OrderLine;
  coreLine?: OrderLine;
  coreChargeDeferred?: boolean;
}

interface SaleTypeMeta {
  label: string;
  color: string;
  icon: ReactElement;
}

const saleTypeConfig: Record<string, SaleTypeMeta> = {
  Repair: {
    label: 'Repair',
    color: 'bg-blue-100 text-blue-700 border-blue-200',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  Exchange: {
    label: 'Exchange',
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
        />
      </svg>
    ),
  },
  Distribution: {
    label: 'Ship',
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    icon: (
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    ),
  },
};

function getProgress(line: OrderLine): number {
  const steps = line.steps ?? [];
  if (steps.length === 0) return 0;
  const completed = steps.filter((s) => s.status === 'completed').length;
  const activeBoost = steps.some((s) => s.status === 'active') ? 0.5 : 0;
  return ((completed + activeBoost) / steps.length) * 100;
}

export function LineTracker({ line, coreLine, coreChargeDeferred }: LineTrackerProps) {
  const config = saleTypeConfig[line.itemSaleType] ?? saleTypeConfig.Distribution;
  const steps = line.steps ?? [];
  const progress = getProgress(line);
  const allDone = steps.length > 0 && steps.every((s) => s.status === 'completed');

  return (
    <div
      className={`
        bg-white rounded-xl border transition-all duration-300
        ${allDone ? 'border-brand-200' : 'border-slate-200 hover:border-slate-300'}
        shadow-sm hover:shadow-md
      `}
    >
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium border ${config.color}`}
              >
                {config.icon}
                {config.label}
              </span>
              {allDone && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-medium bg-brand-100 text-brand-700 border border-brand-200">
                  <svg
                    className="w-3 h-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Complete
                </span>
              )}
            </div>
            <h3 className="text-sm font-semibold text-slate-800 break-words">{line.description}</h3>
            <p className="text-xs text-slate-400 mt-0.5 font-mono">{line.itemNo}</p>
          </div>

          <div className="text-right shrink-0">
            <span className="text-xs font-medium text-brand-600">{line.currentStep}</span>
            {line.quantity > 1 && (
              <p className="text-[10px] text-slate-400 mt-0.5">Qty: {line.quantity}</p>
            )}
          </div>
        </div>

        <div className="mt-3 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-1000 ease-out ${
              allDone ? 'bg-step-completed' : 'bg-gradient-to-r from-step-completed to-step-active'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      <div className="px-5 py-4">
        {steps.map((step, i) => (
          <StepIndicator
            key={step.sequence}
            step={step}
            isLast={i === steps.length - 1 && !coreLine}
          />
        ))}

        {coreLine && <CoreTracker line={coreLine} coreChargeDeferred={coreChargeDeferred} />}
      </div>
    </div>
  );
}
